import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomSignupFieldTypes } from '@shared/welibrary-graphql/types';

import useOptimizers from '@web/ui/components/optimizers/hooks/useOptimizers';
import useOptimizer from '@web/ui/components/optimizers/hooks/useOptimizer';
import LoadingLine from '@web/ui/components/generic/loading/LoadingLine';
import SectionContainer from '@web/ui/components/community/SectionContainer';
import SelectableListItem from '@web/ui/components/forms/SelectableListItem';
import ProgressBar from '@web/ui/components/generic/loading/ProgressBar';
import DisplayGroup from '@web/ui/components/group/DisplayGroup';
import Optimizer from '@web/ui/components/optimizers/Optimizer';
import Button from '@dsc/forms/buttons/Button';
import { SetState } from '@core/types/Utilities';
import { camelize } from '@helpers/string.helpers';

interface IOptimizers {
    groups: any;
    profile: any;
    updateQuery: any;
    loading: any;
    error: any;
    isUserOptimizers?: any;
    isCardOptimize?: any;
    onOptimizerChange?: any;
    onClickOptimizer?: any;
    onSave?: () => void;
    setUseParent?: SetState<boolean>;
    showUseParent?: boolean;
    useParentValue?: boolean;
    customTitle?: any;
    customDescription?: any;
    initialOptimizerId?: any;
    includeLocationAndOrganizationPercentage?: boolean;
}

const Optimizers: React.FC<IOptimizers> = ({
    groups,
    profile,
    updateQuery,
    loading,
    error,
    isUserOptimizers,
    isCardOptimize,
    onOptimizerChange,
    onClickOptimizer,
    showUseParent,
    setUseParent,
    useParentValue,
    customTitle,
    customDescription,
    initialOptimizerId,
    includeLocationAndOrganizationPercentage,
    onSave,
}) => {
    const { t } = useTranslation();
    const { calculatePercentage, setActiveOptimizerId, activeOptimizer, groupsList } =
        useOptimizers(groups, profile, isCardOptimize, initialOptimizerId);
    const {
        percentComplete,
        handleChange,
        handleCustomFieldsChange,
        optimizeState,
        saveChanges,
        groupCustomFields,
        resetPayload,
    } = useOptimizer({
        group: activeOptimizer,
        profile,
        updateQuery,
        isCardOptimize,
        includeLocationAndOrganizationPercentage,
    });

    const { unansweredFields, answeredFields } = (activeOptimizer && optimizeState) ? groupCustomFields?.reduce((sets, field) => {
        const fieldId = field.id || camelize(field.prompt + field.index);

        if (field.type !== CustomSignupFieldTypes.JoinGroup && !optimizeState[fieldId]?.value) {
            sets.unansweredFields.add(fieldId);
        } else {
            sets.answeredFields.add(fieldId);
        }

        return sets

    }, {
        unansweredFields: new Set<string>(),
        answeredFields: new Set<string>(),
    }) : { unansweredFields: new Set<string>(), answeredFields: new Set<string>() };


    const [viewedFields, setViewedFields] = useState(() => {
        const storedViewedFields = localStorage.getItem('viewedFields');

        return storedViewedFields ? new Set([...JSON.parse(storedViewedFields), ...answeredFields]) : answeredFields;
    });


    const markFieldAsViewed = (name: string) => {
        setViewedFields(prev => {
            const newSet = new Set(prev).add(name);
            localStorage.setItem('viewedFields', JSON.stringify([...newSet]));
            return newSet;
        });
    };

    const setAnswersToParent = () => {
        if (useParentValue) resetPayload();
        setUseParent(true);
    };

    useEffect(resetPayload, [useParentValue]);

    const save = e => {
        e?.preventDefault();
        onSave?.();
        onClickOptimizer?.();
        saveChanges();
        if (groupsList.length > 0 || (isCardOptimize && groupsList.length === 0)) {
            setActiveOptimizerId(false);
        }
    };

    const goBack = () => {
        if (groupsList.length > 0 || (isCardOptimize && groupsList.length === 0)) {
            setUseParent?.(false);
            setActiveOptimizerId(false);
        }
    };

    useEffect(() => {
        onOptimizerChange?.(activeOptimizer);
    }, [activeOptimizer]);

    return (
        <section className="user-optimizers">
            <LoadingLine isLoading={loading} error={error} />
            {activeOptimizer ? (
                <section>
                    {groupsList.length > 0 ? (
                        <div className="flex-justify">
                            <button onClick={goBack} type="button" className="back-button">
                                <span aria-hidden />
                                {t('common:global.back')}
                            </button>

                            {showUseParent && (
                                <span className="parent-group-answer-toggle-span">
                                    <Button
                                        onClick={setAnswersToParent}
                                        type="small"
                                        className="parent-answers-toggle"
                                    >
                                        Use Parent Group Answers
                                    </Button>
                                </span>
                            )}
                        </div>
                    ) : (
                        showUseParent && (
                            <div className="flex justify-end">
                                <span className="parent-group-answer-toggle-span">
                                    <Button
                                        onClick={setAnswersToParent}
                                        type="small"
                                        className="parent-answers-toggle"
                                    >
                                        Use Parent Group Answers
                                    </Button>
                                </span>
                            </div>
                        )
                    )}
                    <DisplayGroup
                        title={activeOptimizer.profile.full_name}
                        description={activeOptimizer.profile.short_bio}
                        thumbImage={activeOptimizer.profile.picture}
                    />
                    <ProgressBar completed={activeOptimizer.percentComplete ?? percentComplete} />
                    <Optimizer
                        key={profile?.full_name}
                        isCardOptimize={isCardOptimize}
                        handleChange={activeOptimizer.handleChange ?? handleChange}
                        handleCustomFieldsChange={
                            activeOptimizer.handleCustomFieldsChange ?? handleCustomFieldsChange
                        }
                        optimizeState={activeOptimizer.optimizeState ?? optimizeState}
                        handleSubmit={activeOptimizer.onSave ?? save}
                        groupCustomFields={groupCustomFields}
                        isUserOptimizer={isUserOptimizers}
                        group={activeOptimizer}
                        viewedFields={viewedFields}
                        markFieldAsViewed={markFieldAsViewed}
                        unansweredFields={unansweredFields}
                    />
                </section>
            ) : (
                groupsList.length > 0 && (
                    <SectionContainer title={customTitle ?? 'Group Surveys'}>
                        <p style={{ marginLeft: '10px' }}>
                            {customDescription ??
                                'Optimize recommendations by answering the following surveys:'}
                        </p>
                        {groupsList.map(
                            ({ _id: groupId, profile: { full_name, short_bio, picture } }) => (
                                <SelectableListItem
                                    selected={groupId === activeOptimizer._id}
                                    onClick={() => {
                                        setActiveOptimizerId(groupId);
                                        onClickOptimizer?.();
                                    }}
                                    key={groupId}
                                >
                                    <DisplayGroup
                                        title={full_name}
                                        description={short_bio}
                                        thumbImage={picture}
                                    />
                                    <ProgressBar completed={calculatePercentage(groupId)} />
                                </SelectableListItem>
                            )
                        )}
                    </SectionContainer>
                )
            )}
        </section>
    );
};

export default Optimizers;
